import { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Minus, Plus, Lightbulb, Sparkles } from 'lucide-react';

export default function GameModal({ game, isOpen, onClose, onStartGame }) {
  const [rounds, setRounds] = useState(game?.defaultRounds || 2);

  if (!game) return null;

  const handleStart = () => {
    let gameConfig = { gameId: game.id, rounds };

    if (game.id === 'emoji-challenge' && game.emojiSets) {
      const set = game.emojiSets[Math.floor(Math.random() * game.emojiSets.length)];
      gameConfig.emojis = set;
    }

    if (game.id === 'scene-builder' && game.prompts) {
      const scene = game.prompts[Math.floor(Math.random() * game.prompts.length)];
      gameConfig.scenePrompt = scene;
    }

    onStartGame(gameConfig);
  };

  const maxRounds = game.fixedRounds ? game.defaultRounds : 6;
  const minRounds = 1;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Sheet
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            <SheetHandle />
            <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>

            <GameEmoji>{game.emoji}</GameEmoji>
            <GameName>{game.name}</GameName>
            <GameTagline>{game.tagline}</GameTagline>

            <Description>{game.description}</Description>

            <SectionLabel>How to play</SectionLabel>
            <Steps>
              {game.howToPlay.map((step, i) => (
                <Step key={i}>
                  <StepNum>{i + 1}</StepNum>
                  <StepText>{step}</StepText>
                </Step>
              ))}
            </Steps>

            <TipBox>
              <Lightbulb size={16} />
              <TipText>{game.tip}</TipText>
            </TipBox>

            {game.hasRounds && !game.fixedRounds && (
              <RoundPicker>
                <RoundLabel>Rounds (times around the circle)</RoundLabel>
                <RoundControls>
                  <RoundBtn onClick={() => setRounds(Math.max(minRounds, rounds - 1))} disabled={rounds <= minRounds}>
                    <Minus size={18} />
                  </RoundBtn>
                  <RoundValue>{rounds}</RoundValue>
                  <RoundBtn onClick={() => setRounds(Math.min(maxRounds, rounds + 1))} disabled={rounds >= maxRounds}>
                    <Plus size={18} />
                  </RoundBtn>
                </RoundControls>
              </RoundPicker>
            )}

            <RossNote>
              <Sparkles size={14} />
              Ross can help you draw! Just describe what you want and he'll create it for you.
            </RossNote>

            <StartBtn onClick={handleStart}>
              <Play size={18} />
              Start {game.name}
            </StartBtn>
          </Sheet>
        </>
      )}
    </AnimatePresence>
  );
}

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
`;

const Sheet = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 85vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 20px 20px 0 0;
  padding: 16px 24px 32px;
  z-index: 201;
  padding-bottom: max(32px, env(safe-area-inset-bottom));
`;

const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 auto 16px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 8px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const GameEmoji = styled.div`
  font-size: 56px;
  text-align: center;
  margin-bottom: 8px;
`;

const GameName = styled.h2`
  font-size: 24px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4px;
`;

const GameTagline = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-bottom: 20px;
`;

const Description = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 20px;
`;

const SectionLabel = styled.h3`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-bottom: 12px;
`;

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const StepNum = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
`;

const StepText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text};
`;

const TipBox = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 20px;
  align-items: flex-start;

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.accent};
    margin-top: 2px;
  }
`;

const TipText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const RoundPicker = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  margin-bottom: 20px;
`;

const RoundLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const RoundControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const RoundBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.2s;

  &:disabled {
    opacity: 0.3;
  }

  &:not(:disabled):hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const RoundValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  min-width: 28px;
  text-align: center;
`;

const RossNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 20px;
  padding: 0 4px;

  svg { flex-shrink: 0; }
`;

const StartBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 17px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  }

  &:active {
    transform: scale(0.98);
  }
`;
